import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dialog } from 'primereact/dialog';
import { useState, useMemo } from 'react';
import DailyEntryForm from './DailyEntryForm';
import { Hearts } from 'react-loader-spinner';

const CycleTracker = ({
  currentCycle,
  phaseDates,
  selectedDate,
  showDailyForm,
  currentPhase,
  userName,
  onBack,
  onCycleDateChange,
  onPhaseDateChange,
  onDailyEntrySubmit,
  onDailyFormCancel,
  onDateSelect,
  moodOptions,
  painLevels,
  symptomOptions,
  phaseTypes,
  isLoading,
  isPartnerView,
  activeView,
  setActiveView,
  getCurrentCycleDay,
  getCurrentPhaseInfo
}) => {
  const [selectedPhase, setSelectedPhase] = useState(null);
  const [phaseDialogVisible, setPhaseDialogVisible] = useState(false);
  const [showSetupPanel, setShowSetupPanel] = useState(false);

  const getCurrentDate = () => new Date().toISOString().split('T')[0];

  const getDailyEntry = (date) => currentCycle.dailyEntries.find((entry) => entry.date === date);

  const getPhaseForDate = (date) => currentCycle.phases.find((phase) => date >= phase.startDate && date <= phase.endDate);

  // Generate calendar view for current month
  const calendarData = useMemo(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    const days = [];
    
    // Add empty cells for days before the first of the month
    for (let i = 0; i < startingDay; i++) {
      days.push({ empty: true, key: `empty-${i}` });
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const phase = getPhaseForDate(dateStr);
      const entry = getDailyEntry(dateStr);
      const isToday = dateStr === getCurrentDate();
      
      days.push({
        day,
        date: dateStr,
        phase,
        entry,
        isToday,
        key: dateStr
      });
    }
    
    return {
      monthName: today.toLocaleString('default', { month: 'long', year: 'numeric' }),
      days
    };
  }, [currentCycle.phases, currentCycle.dailyEntries]);

  const currentPhaseInfo = getCurrentPhaseInfo();
  const cycleDay = getCurrentCycleDay();

  const handleCalendarClick = (e) => e.stopPropagation();

  const openPhaseDialog = (phase) => {
    setSelectedPhase(phase);
    setPhaseDialogVisible(true);
  };

  const getPhaseDescription = (phaseType) => {
    const descriptions = {
      Menstrual: {
        subtitle: "The Reset Phase",
        description: "Hormones are at their lowest. Your body is shedding the uterine lining. Rest and self-care are essential.",
        dos: [
          "Prioritize rest and gentle movement",
          "Stay hydrated and eat iron-rich foods",
          "Use heat therapy for cramps",
          "Be gentle with yourself emotionally"
        ],
        donts: [
          "Don't push through intense workouts",
          "Avoid excessive caffeine and salt",
          "Don't ignore your body's signals",
          "Skip scheduling stressful events"
        ],
        tip: "This is your body's way of pressing reset. Honor the slowdown."
      },
      Follicular: {
        subtitle: "The Rising Energy Phase",
        description: "Estrogen is climbing, bringing renewed energy and optimism. Your brain is primed for creativity and new beginnings.",
        dos: [
          "Start new projects and set goals 🎯",
          "Try new workouts or activities 💪",
          "Schedule social events and meetings 🗓️",
          "Feed your creativity and curiosity ✨"
        ],
        donts: [
          "Don't waste this productive window",
          "Avoid staying in your comfort zone",
          "Don't neglect physical activity",
          "Skip opportunities for connection"
        ],
        tip: "Your energy is building—channel it into what matters most to you."
      },
      Ovulation: {
        subtitle: "The Peak Phase",
        description: "Estrogen peaks and testosterone rises briefly. You're at your most confident, communicative, and magnetic.",
        dos: [
          "Have important conversations",
          "Present ideas and speak up",
          "Connect deeply with loved ones",
          "Embrace your natural radiance "
        ],
        donts: [
          "Don't hide or play small",
          "Avoid isolation during this social peak",
          "Don't make impulsive decisions",
          "Skip protection if not family planning"
        ],
        tip: "You're glowing from the inside out. Let yourself be seen."
      },
      Luteal: {
        subtitle: "The Winding Down Phase",
        description: "Progesterone rises, then falls if pregnancy doesn't occur. Energy turns inward. PMS symptoms may appear in the late luteal phase.",
        dos: [
          "Focus on completing tasks",
          "Practice extra self-compassion",
          "Prepare healthy snacks for cravings",
          "Create calm and cozy spaces"
        ],
        donts: [
          "Don't start major new projects",
          "Avoid overcommitting socially",
          "Don't dismiss your feelings",
          "Skip self-care routines"
        ],
        tip: "Your inner critic may get louder—counter it with extra kindness."
      }
    };
    return descriptions[phaseType] || null;
  };

  // Statistics calculations
  const stats = useMemo(() => {
    const entries = currentCycle.dailyEntries || [];
    const recentEntries = entries.slice(-14);
    
    // Mood distribution
    const moodCounts = {};
    recentEntries.forEach(e => {
      if (e.mood) {
        moodCounts[e.mood] = (moodCounts[e.mood] || 0) + 1;
      }
    });
    
    // Most common symptoms
    const symptomCounts = {};
    recentEntries.forEach(e => {
      (e.symptoms || []).forEach(s => {
        symptomCounts[s] = (symptomCounts[s] || 0) + 1;
      });
    });
    
    const topSymptoms = Object.entries(symptomCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3);

    return {
      totalEntries: entries.length,
      moodCounts,
      topSymptoms
    };
  }, [currentCycle.dailyEntries]);

  if (isLoading) {
    return (
      <div className="cs-tracker-page">
        <div className="cs-loading-container">
          <Hearts height="80" width="80" color="#f97316" ariaLabel="hearts-loading" visible={true} />
          <p className="cs-loading-text">Loading your cycle data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="cs-tracker-page">
      {/* Header */}
      <header className="cs-tracker-header">
        <div className="cs-header-left">
          <Button
            icon="pi pi-arrow-left"
            onClick={onBack}
            className="cs-back-btn"
            rounded
            text
            aria-label="Go back"
          />
          <div className="cs-header-info">
            <h1>{isPartnerView ? `${userName}'s Cycle` : 'My Cycle'}</h1>
            {cycleDay && (
              <span className="cs-cycle-day-badge">
                Day {cycleDay}
              </span>
            )}
          </div>
        </div>
        <Button
          icon="pi pi-cog"
          onClick={() => setShowSetupPanel(!showSetupPanel)}
          className="cs-settings-btn"
          rounded
          text
          aria-label="Cycle settings"
          tooltip="Cycle Settings"
          tooltipOptions={{ position: 'left' }}
        />
      </header>

      {/* Current Phase Card */}
      {currentPhaseInfo && (
        <div 
          className="cs-current-phase-card"
          style={{ '--phase-color': currentPhaseInfo.color }}
          onClick={() => openPhaseDialog(phaseTypes.find(p => p.name === currentPhaseInfo.name))}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              openPhaseDialog(phaseTypes.find(p => p.name === currentPhaseInfo.name));
            }
          }}
          aria-label={`Current phase: ${currentPhaseInfo.name}. Click for details.`}
        >
          <div className="cs-phase-indicator" style={{ backgroundColor: currentPhaseInfo.color }}></div>
          <div className="cs-phase-content">
            <span className="cs-phase-label">Current Phase</span>
            <h2 className="cs-phase-name">{currentPhaseInfo.name}</h2>
          </div>
          <i className="pi pi-chevron-right cs-phase-arrow"></i>
        </div>
      )}

      {/* Navigation Tabs */}
      <nav className="cs-nav-tabs" role="tablist" aria-label="View selection">
        <button 
          className={`cs-nav-tab ${activeView === 'calendar' ? 'active' : ''}`}
          onClick={() => setActiveView('calendar')}
          role="tab"
          aria-selected={activeView === 'calendar'}
          aria-controls="calendar-panel"
        >
          <i className="pi pi-calendar"></i>
          <span>Calendar</span>
        </button>
        <button 
          className={`cs-nav-tab ${activeView === 'insights' ? 'active' : ''}`}
          onClick={() => setActiveView('insights')}
          role="tab"
          aria-selected={activeView === 'insights'}
          aria-controls="insights-panel"
        >
          <i className="pi pi-chart-bar"></i>
          <span>Insights</span>
        </button>
        <button 
          className={`cs-nav-tab ${activeView === 'phases' ? 'active' : ''}`}
          onClick={() => setActiveView('phases')}
          role="tab"
          aria-selected={activeView === 'phases'}
          aria-controls="phases-panel"
        >
          <i className="pi pi-info-circle"></i>
          <span>Phases</span>
        </button>
      </nav>

      {/* Main Content */}
      <main className="cs-main-content">
        {/* Calendar View */}
        {activeView === 'calendar' && (
          <div className="cs-calendar-view">
            <div className="cs-calendar-header">
              <h2>{calendarData.monthName}</h2>
            </div>
            
            <div className="cs-calendar-weekdays">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="cs-weekday">{day}</div>
              ))}
            </div>
            
            <div className="cs-calendar-grid">
              {calendarData.days.map((dayData) => {
                if (dayData.empty) {
                  return <div key={dayData.key} className="cs-day-cell empty"></div>;
                }
                
                return (
                  <div
                    key={dayData.key}
                    className={`cs-day-cell ${dayData.isToday ? 'today' : ''} ${dayData.entry ? 'has-entry' : ''}`}
                    style={dayData.phase ? { 
                      backgroundColor: `${dayData.phase.color}15`,
                      borderColor: dayData.phase.color 
                    } : {}}
                    onClick={() => onDateSelect(dayData.date, dayData.phase?.name)}
                  >
                    <span className="cs-day-number">{dayData.day}</span>
                    {dayData.phase && (
                      <div 
                        className="cs-day-phase-dot" 
                        style={{ backgroundColor: dayData.phase.color }}
                      ></div>
                    )}
                    {dayData.entry && (
                      <div className="cs-day-entry-indicator">
                        <span className="cs-entry-mood">
                          {moodOptions.find(m => m.value === dayData.entry.mood)?.emoji || '📝'}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Phase Legend */}
            <div className="cs-phase-legend">
              {phaseTypes.map(phase => (
                <div 
                  key={phase.name} 
                  className="cs-legend-item"
                  onClick={() => openPhaseDialog(phase)}
                >
                  <span 
                    className="cs-legend-dot" 
                    style={{ backgroundColor: phase.color }}
                  ></span>
                  <span className="cs-legend-label">{phase.name}</span>
                </div>
              ))}
            </div>

            {/* Quick Add Button */}
            <button 
              className="cs-quick-add-btn"
              onClick={() => onDateSelect(getCurrentDate(), getPhaseForDate(getCurrentDate())?.name)}
            >
              <i className="pi pi-plus"></i>
              <span>Log Today</span>
            </button>
          </div>
        )}

        {/* Insights View */}
        {activeView === 'insights' && (
          <div className="cs-insights-view">
            <div className="cs-insights-grid">
              {/* Entries Count */}
              <div className="cs-insight-card">
                <div className="cs-insight-icon">
                  <i className="pi pi-file"></i>
                </div>
                <div className="cs-insight-data">
                  <span className="cs-insight-value">{stats.totalEntries}</span>
                  <span className="cs-insight-label">Total Entries</span>
                </div>
              </div>

              {/* Cycle Length */}
              <div className="cs-insight-card">
                <div className="cs-insight-icon">
                  <i className="pi pi-calendar"></i>
                </div>
                <div className="cs-insight-data">
                  <span className="cs-insight-value">{currentCycle.averageCycleLength || 28}</span>
                  <span className="cs-insight-label">Cycle Length</span>
                </div>
              </div>

              {/* Current Day */}
              <div className="cs-insight-card">
                <div className="cs-insight-icon">
                  <i className="pi pi-sun"></i>
                </div>
                <div className="cs-insight-data">
                  <span className="cs-insight-value">{cycleDay || '—'}</span>
                  <span className="cs-insight-label">Current Day</span>
                </div>
              </div>

              {/* Current Phase */}
              <div className="cs-insight-card">
                <div className="cs-insight-icon" style={{ color: currentPhaseInfo?.color }}>
                  <i className="pi pi-circle-fill"></i>
                </div>
                <div className="cs-insight-data">
                  <span className="cs-insight-value">{currentPhaseInfo?.name || 'Not set'}</span>
                  <span className="cs-insight-label">Current Phase</span>
                </div>
              </div>
            </div>

            {/* Top Symptoms */}
            {stats.topSymptoms.length > 0 && (
              <div className="cs-stats-section">
                <h3>Most Logged Symptoms</h3>
                <div className="cs-symptom-bars">
                  {stats.topSymptoms.map(([symptom, count]) => (
                    <div key={symptom} className="cs-symptom-bar-item">
                      <div className="cs-symptom-info">
                        <span className="cs-symptom-name">
                          {symptomOptions.find(s => s.value === symptom)?.emoji} {symptom}
                        </span>
                        <span className="cs-symptom-count">{count}x</span>
                      </div>
                      <div className="cs-symptom-bar">
                        <div 
                          className="cs-symptom-bar-fill" 
                          style={{ width: `${(count / stats.totalEntries) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recent Mood Distribution */}
            {Object.keys(stats.moodCounts).length > 0 && (
              <div className="cs-stats-section">
                <h3>Recent Moods</h3>
                <div className="cs-mood-grid">
                  {Object.entries(stats.moodCounts).map(([mood, count]) => (
                    <div key={mood} className="cs-mood-chip">
                      <span className="cs-mood-emoji">
                        {moodOptions.find(m => m.value === mood)?.emoji}
                      </span>
                      <span className="cs-mood-label">{mood}</span>
                      <span className="cs-mood-count">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Phases View */}
        {activeView === 'phases' && (
          <div className="cs-phases-view">
            <p className="cs-phases-intro">
              Tap on any phase to learn more about what's happening in your body and how to best support yourself.
            </p>
            
            <div className="cs-phases-grid">
              {phaseTypes.map(phase => (
                <div 
                  key={phase.name}
                  className="cs-phase-card"
                  style={{ '--phase-color': phase.color }}
                  onClick={() => openPhaseDialog(phase)}
                >
                  <div 
                    className="cs-phase-card-icon"
                    style={{ backgroundColor: `${phase.color}20`, color: phase.color }}
                  >
                    <i className="pi pi-circle-fill"></i>
                  </div>
                  <h3>{phase.name}</h3>
                  <span className="cs-phase-days">Days {phase.days}</span>
                  
                  {/* Phase Date Inputs */}
                  <div className="cs-phase-dates" onClick={handleCalendarClick}>
                    <div className="cs-date-input-group">
                      <label>Start</label>
                      <Calendar
                        value={phaseDates[phase.name]?.startDate || null}
                        onChange={(e) => onPhaseDateChange(phase.name, 'startDate', e.value)}
                        dateFormat="M dd"
                        showIcon
                        placeholder="Set date"
                        className="cs-mini-calendar"
                      />
                    </div>
                    <div className="cs-date-input-group">
                      <label>End</label>
                      <Calendar
                        value={phaseDates[phase.name]?.endDate || null}
                        onChange={(e) => onPhaseDateChange(phase.name, 'endDate', e.value)}
                        dateFormat="M dd"
                        showIcon
                        placeholder="Set date"
                        className="cs-mini-calendar"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Setup Panel (Slide-in) */}
      {showSetupPanel && (
        <div className="cs-setup-overlay" onClick={() => setShowSetupPanel(false)}>
          <div className="cs-setup-panel" onClick={e => e.stopPropagation()}>
            <div className="cs-setup-header">
              <h2>Cycle Settings</h2>
              <Button
                icon="pi pi-times"
                onClick={() => setShowSetupPanel(false)}
                className="cs-close-panel-btn"
                rounded
                text
              />
            </div>
            
            <div className="cs-setup-content">
              <div className="cs-setup-group">
                <label>Cycle Start Date</label>
                <Calendar
                  value={currentCycle.cycleStart ? new Date(currentCycle.cycleStart) : null}
                  onChange={(e) => onCycleDateChange('cycleStart', e.value)}
                  dateFormat="MM dd, yy"
                  showIcon
                  placeholder="Select start date"
                  className="cs-full-calendar"
                />
              </div>
              
              <div className="cs-setup-group">
                <label>Expected Cycle End</label>
                <Calendar
                  value={currentCycle.cycleEnd ? new Date(currentCycle.cycleEnd) : null}
                  onChange={(e) => onCycleDateChange('cycleEnd', e.value)}
                  dateFormat="MM dd, yy"
                  showIcon
                  placeholder="Select end date"
                  className="cs-full-calendar"
                />
              </div>

              <div className="cs-setup-info">
                <i className="pi pi-info-circle"></i>
                <p>Set your cycle dates to enable accurate phase tracking and predictions.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Daily Entry Modal */}
      {showDailyForm && (
        <div className="cs-modal-overlay" onClick={onDailyFormCancel}>
          <div className="cs-modal-content" onClick={e => e.stopPropagation()}>
            <div className="cs-modal-header">
              <h2>
                <span className="cs-modal-date">
                  {new Date(selectedDate).toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    month: 'short', 
                    day: 'numeric' 
                  })}
                </span>
              </h2>
              {currentPhase && (
                <span 
                  className="cs-modal-phase-badge"
                  style={{ backgroundColor: phaseTypes.find(p => p.name === currentPhase)?.color }}
                >
                  {currentPhase}
                </span>
              )}
              <Button
                icon="pi pi-times"
                onClick={onDailyFormCancel}
                className="cs-modal-close-btn"
                rounded
                text
              />
            </div>

            <DailyEntryForm
              date={selectedDate}
              existingEntry={getDailyEntry(selectedDate)}
              moodOptions={moodOptions}
              painLevels={painLevels}
              symptomOptions={symptomOptions}
              onSubmit={onDailyEntrySubmit}
              onCancel={onDailyFormCancel}
            />
          </div>
        </div>
      )}

      {/* Phase Info Dialog */}
      <Dialog
        visible={phaseDialogVisible}
        onHide={() => setPhaseDialogVisible(false)}
        className="cs-phase-dialog"
        dismissableMask
        showHeader={false}
        style={{ width: '90%', maxWidth: '500px' }}
      >
        {selectedPhase && getPhaseDescription(selectedPhase.name) && (
          <div className="cs-phase-dialog-content">
            <button 
              className="cs-dialog-close"
              onClick={() => setPhaseDialogVisible(false)}
            >
              <i className="pi pi-times"></i>
            </button>
            
            <div 
              className="cs-dialog-header"
              style={{ backgroundColor: `${selectedPhase.color}20` }}
            >
              <div 
                className="cs-dialog-emoji" 
                style={{ backgroundColor: selectedPhase.color, color: 'white' }}
              >
                <i className="pi pi-circle-fill"></i>
              </div>
              <div>
                <h2>{selectedPhase.name} Phase</h2>
                <p className="cs-dialog-subtitle">
                  {getPhaseDescription(selectedPhase.name).subtitle}
                </p>
              </div>
            </div>

            <div className="cs-dialog-body">
              <p className="cs-dialog-description">
                {getPhaseDescription(selectedPhase.name).description}
              </p>

              <div className="cs-dialog-section">
                <h3><span className="cs-section-icon cs-do-icon">✓</span> Do's</h3>
                <ul>
                  {getPhaseDescription(selectedPhase.name).dos.map((item, idx) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              </div>

              <div className="cs-dialog-section">
                <h3><span className="cs-section-icon cs-dont-icon">✕</span> Don'ts</h3>
                <ul>
                  {getPhaseDescription(selectedPhase.name).donts.map((item, idx) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              </div>

              <div className="cs-dialog-tip">
                <div className="cs-tip-icon">
                  <i className="pi pi-lightbulb"></i>
                </div>
                <p>{getPhaseDescription(selectedPhase.name).tip}</p>
              </div>
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
};

export default CycleTracker;
